import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useTaskSchema } from "components/Tasks/createTaskSchema";
import { TaskDialog } from "components/Tasks/TaskDialog";
import { TaskFormTenantProvider } from "components/Tasks/TaskFormTenantContext";
import { TaskFormType } from "components/Tasks/types";
import { Frequency, TaskStatus } from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  CREATE_TASK_FOR_NOTIFICATION,
  GET_MY_NOTIFICATIONS,
  GET_MY_UNREAD_NOTIFICATION_COUNT,
  GET_NOTIFICATION_USER,
} from "hooks/useNotifications";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { markdownToTaskFields } from "util/markdownToPlainText";
import { v4 } from "uuid";

type CreateLinkedTaskDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  notificationId: string;
  tenantId: string;
  defaultTitle: string;
  /** Notification body markdown — split into a plain-text description and links. */
  bodyMarkdown: string;
  defaultFacilityId: string | null;
};

export function CreateLinkedTaskDialog({
  open,
  onClose,
  onCreated,
  notificationId,
  tenantId,
  defaultTitle,
  bodyMarkdown,
  defaultFacilityId,
}: CreateLinkedTaskDialogProps) {
  const alerts = useAlerts();
  const yupSchema = useTaskSchema();
  const { user } = useCurrentUser();

  const [createTaskForNotification, { loading }] = useMutation(CREATE_TASK_FOR_NOTIFICATION, {
    refetchQueries: [GET_MY_NOTIFICATIONS, GET_MY_UNREAD_NOTIFICATION_COUNT, GET_NOTIFICATION_USER],
  });

  const [deleteDocument] = useDeleteDocumentMutation({
    onError: () => {
      alerts.error("Failed to delete document");
    },
  });

  const { description, links } = useMemo(() => markdownToTaskFields(bodyMarkdown), [bodyMarkdown]);

  const initialValues = useMemo<TaskFormType>(
    () => ({
      title: defaultTitle,
      description,
      assignee: user
        ? {
            id: user.id,
            email: user.email ?? null,
            person: user.person ?? null,
          }
        : null,
      deadline: null,
      frequency: Frequency.Once,
      taskStatus: TaskStatus.Open,
      interval: 1,
      reminderDays: [],
      subtasks: [],
      facilityIds: defaultFacilityId ? [defaultFacilityId] : [],
      tags: [],
      documents: [],
      links: links.map((url) => ({ url, id: v4() })),
      watchers: [],
      completedOn: null,
      completionTiming: null,
      completionDeltaDays: null,
      isSuggested: false,
    }),
    [defaultTitle, description, links, defaultFacilityId, user]
  );

  const handleClose = useCallback(
    (formValues: TaskFormType) => {
      if (formValues.documents) {
        formValues.documents.forEach((document) => {
          if (document.id) {
            deleteDocument({ variables: { id: document.id } });
          }
        });
      }
      onClose();
    },
    [deleteDocument, onClose]
  );

  const handleSave = useCallback(
    async (data: TaskFormType) => {
      try {
        await createTaskForNotification({
          variables: {
            notificationId,
            input: {
              tenantId,
              title: data.title,
              description: data.description,
              assigneeId: data.assignee?.id,
              deadline: data.deadline ? new Date(data.deadline).toISOString().slice(0, 10) : null,
              reminderDays: data.reminderDays.map((reminder) => reminder.days),
              frequency: data.frequency,
              interval: data.interval,
              watchers: data.watchers.map((watcher) => watcher.id),
              facilityIds: data.facilityIds,
              documentIds: data.documents.map((document) => document.id),
              links: data.links.map((link) => link.url),
              subtasks: data.subtasks
                .filter((subTask) => subTask.title?.trim() !== "")
                .map((subTask) => ({
                  title: subTask.title,
                  completedAt: subTask.completed ? DateTime.now() : null,
                })),
              tags: data.tags.filter((tag) => tag.name.trim() !== ""),
            },
          },
        });
        alerts.success("Task created and linked to notification");
        await onCreated();
      } catch (err) {
        alerts.error("Failed to create task", err);
      }
    },
    [alerts, createTaskForNotification, notificationId, onCreated, tenantId]
  );

  return (
    <TaskFormTenantProvider tenantId={tenantId}>
      <TaskDialog
        isLoading={false}
        initialValues={initialValues}
        validationResolver={yupResolver(yupSchema) as any}
        onSubmit={handleSave}
        onClose={handleClose}
        isSubmitting={loading}
        open={open}
        cancelButtonText="Cancel"
        submitButtonText="Create task"
        showCommentsSection={false}
        autoFocusTitle
        disableRestoreFocus
      />
    </TaskFormTenantProvider>
  );
}
